/**
 * Границы экономических зон на карте кабинета.
 *
 * Отвечают на два разных вопроса, и оба задаёт капитан: ГДЕ проходит рубеж
 * (линия) и В ЧЬЕЙ воде он стоит (строка карточки отсчёта).
 *
 * Источник — Marine Regions v12 (CC-BY). Он же прямо заявляет, что его
 * геометрия не авторитетна и не предназначена для навигационных и юридических
 * целей; оговорка приезжает КЛЮЧОМ в `meta` ответа и рисуется в легенде — на
 * слое, по которому решают, пересекать ли границу, она обязана быть видна, а не
 * лежать в документации, которую никто не откроет.
 */

import { mapColorOf } from '../brand/mapColors'
import { t } from '../i18n'
import { OVERLAY_PREFIX } from './layerOrder'

export interface EezLineProps {
  line_type?: string | null
  line_name?: string | null
  sovereign1?: string | null
  sovereign2?: string | null
  territory1?: string | null
  territory2?: string | null
  source?: string | null
  doc_date?: string | null
}

export interface EezStyle {
  color: string
  /** Толщина в пикселях на опорном зуме (см. eezWidthExpr). */
  width: number
  /** Штриховка в единицах толщины; `null` — сплошная. */
  dash: [number, number] | null
}

/** Начертания по типу рубежа.
 *
 *  Сплошная — там, где рубеж УЛАЖЕН (договор, решение суда, объявленный
 *  200-мильный предел). Штрих — там, где линия ПОСТРОЕНА (медиана, связка):
 *  одинаковое с договором начертание уравняло бы их в правах на глаз.
 *
 *  ПОНЯТИЯ «СПОРНЫЙ РУБЕЖ» ЗДЕСЬ НЕТ (решение владельца 07.09.2026). До этого
 *  типы `Unsettled*`/`Unilateral*` красились своим тревожным цветом и точечным
 *  штрихом. Российских рубежей среди них нет ни одного — Южные Курилы
 *  переразмечены в ИЭЗ России ещё правкой 29.08.2026 (`src/geo/eez_overrides.py`),
 *  — а чужой спор (Япония / Республика Корея, Германия / Дания) продукт не
 *  комментирует: линия рисуется, стороны в карточке остаются как в источнике.
 *
 *  Неулаженные типы перечислены ПОИМЁННО, а не отданы запасному начертанию:
 *  запасное существует для типов, которых мы ещё не видели, и свалив туда
 *  известные, мы потеряли бы разницу между «медиана» и «незнакомая линия».
 */
const STYLES: Record<string, Omit<EezStyle, 'color'>> = {
  // Внешний предел зоны — главная линия слоя, поэтому самая заметная.
  '200 NM': { width: 1.6, dash: null },
  '12 NM': { width: 1.0, dash: null },
  Treaty: { width: 1.4, dash: null },
  'Court ruling': { width: 1.4, dash: null },
  'Joint regime': { width: 1.4, dash: [3, 1.5] },
  'Median line': { width: 1.0, dash: [2, 2] },
  'Connection line': { width: 1.0, dash: [2, 2] },
  // Неулаженные — начертанием по их СУТИ: сухопутный рубеж сплошной, медиана
  // штриховая, одностороннее заявление — построенная линия, то есть штрих.
  'Unsettled (land)': { width: 1.4, dash: null },
  'Unsettled (maritime)': { width: 1.4, dash: null },
  'Unsettled median line (land)': { width: 1.0, dash: [2, 2] },
  'Unilateral claim (undisputed)': { width: 1.0, dash: [2, 2] },
}

/** Незнакомый тип — не повод ронять слой. Фильтр загрузки ИСКЛЮЧАЮЩИЙ, значит
 *  новый тип рубежа попадёт на карту раньше, чем мы про него узнаем; загрузчик
 *  назовёт его в журнале, а карта обязана нарисовать его тем временем. */
const FALLBACK: Omit<EezStyle, 'color'> = { width: 1.0, dash: null }

/** Тон принадлежит СЛОЮ, а не типу линии: у рубежей он один на все типы. */
export function eezStyle(lineType: string | null | undefined): EezStyle {
  const key = (lineType ?? '').trim()
  return { color: mapColorOf('eez'), ...(STYLES[key] ?? FALLBACK) }
}

// --------------------------------------------------------------------------- //
// Названия стран
// --------------------------------------------------------------------------- //

/** Имя страны из источника → ключ каталога перевода.
 *
 *  Источник называет страны ПО-АНГЛИЙСКИ и кода ISO у линий не даёт вовсе, так
 *  что ключ приходится строить из имени. Одно ключевое пространство на линии и
 *  на зоны: два разошлись бы, и половина карты осталась бы непереведённой.
 */
export function countrySlug(name: string | null | undefined): string {
  return (name ?? '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '')
}

/** Название страны на языке кабинета, а незнакомой — КАК В ИСТОЧНИКЕ.
 *
 *  То же правило, что у видов рыб реестров РФМО: код, в котором не уверены,
 *  показывается сам собой. Выдуманный перевод пережил бы верификацию и остался
 *  бы в каталоге навсегда.
 */
export function countryName(name: string | null | undefined): string {
  if (!name) return ''
  const key = `country.${countrySlug(name)}`
  const got = t(key)
  return got === key ? name : got
}

/** Название типа рубежа на языке кабинета.
 *
 *  Незнакомый тип показывается КАК В ИСТОЧНИКЕ, а не ключом каталога:
 *  «eez.type.deep_seabed_corridor» на карточке не значит ничего, а английская
 *  строка источника — значит. То же правило, что у названий стран.
 */
export function eezTypeLabel(lineType: string | null | undefined): string {
  const raw = (lineType ?? '').trim()
  if (!raw) return ''
  const key = `eez.type.${countrySlug(raw)}`
  const got = t(key)
  return got === key ? raw : got
}

/** Подпись рубежа: обе его стороны.
 *
 *  У 200-мильного предела второй стороны НЕТ — там открытое море, и источник
 *  честно кладёт туда пустоту. Подставлять что-либо нельзя: «Россия — ???»
 *  прочиталось бы как спорный рубеж с кем-то.
 */
export function eezLineLabel(props: EezLineProps): string {
  const a = countryName(props.sovereign1)
  const b = countryName(props.sovereign2)
  if (a && b) return `${a} — ${b}`
  const one = a || b
  const type = (props.line_type ?? '').trim()
  if (one && (type === '200 NM' || type === '12 NM')) {
    return `${t(`eez.type.${countrySlug(type)}`)} · ${one}`
  }
  return one
}

// --------------------------------------------------------------------------- //
// Зона под точкой
// --------------------------------------------------------------------------- //

export interface ZoneAnswer {
  status: 'eez' | 'high_seas' | 'unknown'
  pol_type?: string | null
  sovereign?: string | null
  geoname?: string | null
  iso?: string | null
}

/** Строка «в чьей зоне точка» для карточки отсчёта.
 *
 *  `null` — только для `unknown` (зоны не загружены): соврать про право хуже,
 *  чем промолчать. Открытое море, наоборот, СТРОКУ ДАЁТ — это настоящий ответ,
 *  и капитану он важнее прочих: охотоморская и беринговоморская «дыры» живут по
 *  другим правилам, а молчание он прочитал бы как «мы не знаем».
 */
export function zoneLine(zone: ZoneAnswer | null | undefined): string | null {
  if (!zone || zone.status === 'unknown') return null
  if (zone.status === 'high_seas') return t('eez.zone.highSeas')

  const pol = (zone.pol_type ?? '').trim()
  if (pol === '200NM') {
    const who = countryName(zone.sovereign)
    return who ? `${t('eez.zone.eez')} · ${who}` : t('eez.zone.eez')
  }
  // Совместный режим и спорная зона — НЕ зона одной страны, и назвать их «ИЭЗ
  // России» значило бы соврать про право. Сторон у них две и более, а
  // структурно источник отдаёт только одну — поэтому вторую половину фразы
  // берём его же названием, как есть, а не собираем сами.
  const kind = pol ? t(`eez.pol.${countrySlug(pol)}`) : t('eez.zone.generic')
  const detail = zone.geoname || countryName(zone.sovereign)
  return detail ? `${kind} · ${detail}` : kind
}

// --------------------------------------------------------------------------- //
// Слои
// --------------------------------------------------------------------------- //

const EEZ_PREFIX = OVERLAY_PREFIX + 'eez-'

/** id слоя. Три части на весь слой: белая подложка, линии и подписи.
 *
 *  Слой на КАЖДЫЙ тип рубежа не нужен, хотя `line-dasharray` выражений и не
 *  принимает: типов немного, и они разведены по трём слоям штриховки ниже —
 *  сплошные, штриховые и точечные. Это меньше слоёв, чем типов, и не растёт с
 *  появлением нового типа.
 */
export function eezLayerId(part: 'casing' | 'line' | 'label'): string {
  return `${EEZ_PREFIX}${part}`
}

/** id слоя одной штриховки: `line-dasharray` в MapLibre выражений не принимает,
 *  поэтому начертание — это отдельный слой, а не свойство фичи. */
export function eezDashLayerId(dash: EezDashGroup): string {
  return `${EEZ_PREFIX}line-${dash}`
}

/** Какому слою штриховки принадлежит тип рубежа. */
export function eezDashGroup(lineType: string | null | undefined): EezDashGroup {
  return eezStyle(lineType).dash ? 'dashed' : 'solid'
}

/** С какого зума подписывать рубежи. На обзорном в кадр попадает весь бассейн,
 *  ярлыки стран сливаются в кашу — там рубежи различает легенда, а не карта.
 *  Та же мысль, что у `ISOBATH_LABEL_MIN_ZOOM`. */
export const EEZ_LABEL_MIN_ZOOM = 5

/** Толщина линии по зуму — как у изобат: на обзорных тоньше, на рабочих в
 *  номинал. `extra` — прибавка белой подложки, она обязана быть шире линии. */
export function eezWidthExpr(width: number, extra = 0): unknown {
  const at = (k: number) => width * k + extra
  return ['interpolate', ['linear'], ['zoom'], 3, at(0.7), 7, at(1), 12, at(1.6)]
}

// --------------------------------------------------------------------------- //
// Подготовка фич к отрисовке
// --------------------------------------------------------------------------- //

/** Служебные свойства, дописанные фичам перед укладкой в источник.
 *
 *  Начертание, цвет, толщина и подпись считаются ЗДЕСЬ, а не выражениями
 *  MapLibre, по двум причинам. Во-первых, `line-dasharray` выражений не
 *  принимает вовсе — группу штриховки всё равно пришлось бы знать заранее.
 *  Во-вторых, подпись зависит от ЯЗЫКА кабинета, а язык в стиль карты не
 *  приезжает: собранная выражением, она осталась бы английской навсегда.
 */
export interface EezDecor {
  _dash: EezDashGroup
  _color: string
  _width: number
  _label: string
}

export function decorateEez(
  fc: GeoJSON.FeatureCollection,
): GeoJSON.FeatureCollection {
  return {
    ...fc,
    features: (fc.features ?? []).map(f => {
      const props = (f.properties ?? {}) as EezLineProps
      const style = eezStyle(props.line_type)
      return {
        ...f,
        properties: {
          ...props,
          _dash: eezDashGroup(props.line_type),
          _color: style.color,
          _width: style.width,
          _label: eezLineLabel(props),
        },
      }
    }),
  }
}

/** Штриховка группы, в единицах толщины линии. */
export const EEZ_DASH_ARRAY: Record<EezDashGroup, [number, number] | null> = {
  solid: null,
  dashed: [2, 2],
}

/** Начертания, на которые режется слой рубежей. Список ОДИН на всех: его
 *  читают и укладка слоёв, и обработчики клика/наведения в `MapCanvas`, и
 *  копии уже расходились — при упразднении спорных рубежей 07.09.2026 группа
 *  `dotted` осталась в двух списках `MapCanvas` и опрашивала несуществующий
 *  слой. Молча: `getLayer` вернул бы `undefined`, и обработчик просто не
 *  сработал бы. */
export const EEZ_DASH_GROUPS = ['solid', 'dashed'] as const

export type EezDashGroup = (typeof EEZ_DASH_GROUPS)[number]

/** Толщина по свойству фичи. Зум СНАРУЖИ — обратный порядок MapLibre отвергает
 *  целиком, и TypeScript этого не видит (та же ловушка, что у изобат). */
export function eezWidthByPropExpr(extra = 0): unknown {
  const w = ['get', '_width']
  const at = (k: number) => (extra ? ['+', ['*', w, k], extra] : ['*', w, k])
  return ['interpolate', ['linear'], ['zoom'], 3, at(0.7), 7, at(1), 12, at(1.6)]
}
